import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { AlertCircle, CheckCircle2, XCircle, X, Info } from 'lucide-react';

// Tipos de toast
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
}

interface ToastContextType {
    showToast: (type: ToastType, title: string, message?: string, duration?: number) => void;
    showError: (title: string, message?: string) => void;
    showSuccess: (title: string, message?: string) => void;
    showWarning: (title: string, message?: string) => void;
    showInfo: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType>({} as ToastContextType);

export const useToast = () => useContext(ToastContext);

// Configurações visuais por tipo
const toastConfig: Record<ToastType, { icon: React.ReactNode; bgColor: string; borderColor: string; textColor: string }> = {
    success: {
        icon: <CheckCircle2 size={20} />,
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        textColor: 'text-green-800'
    },
    error: {
        icon: <XCircle size={20} />,
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-800'
    },
    warning: {
        icon: <AlertCircle size={20} />,
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        textColor: 'text-yellow-800'
    },
    info: {
        icon: <Info size={20} />,
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-800'
    }
};

// Componente de Toast individual
const ToastItem: React.FC<{ toast: Toast; onClose: (id: string) => void }> = ({ toast, onClose }) => {
    const config = toastConfig[toast.type];

    React.useEffect(() => {
        const timer = setTimeout(() => {
            onClose(toast.id);
        }, toast.duration || 5000);

        return () => clearTimeout(timer);
    }, [toast.id, toast.duration, onClose]);

    return (
        <div
            className={`${config.bgColor} ${config.borderColor} ${config.textColor} border rounded-lg p-4 shadow-lg flex items-start gap-3 max-w-sm animate-slide-in`}
            role="alert"
        >
            <div className="flex-shrink-0 mt-0.5">
                {config.icon}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{toast.title}</p>
                {toast.message && (
                    <p className="text-sm opacity-80 mt-1">{toast.message}</p>
                )}
            </div>
            <button
                onClick={() => onClose(toast.id)}
                className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                aria-label="Fechar"
            >
                <X size={16} />
            </button>
        </div>
    );
};

// Provider do Toast
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showToast = useCallback((type: ToastType, title: string, message?: string, duration: number = 5000) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newToast: Toast = { id, type, title, message, duration };
        setToasts(prev => [...prev, newToast]);
    }, []);

    // Listen for custom events from AuthContext (which can't use useToast directly)
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail && detail.type && detail.title) {
                const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                setToasts(prev => [...prev, { id, type: detail.type, title: detail.title, message: detail.message, duration: detail.type === 'error' ? 7000 : 5000 }]);
            }
        };
        window.addEventListener('app-toast', handler);
        return () => window.removeEventListener('app-toast', handler);
    }, []);

    const showError = useCallback((title: string, message?: string) => {
        showToast('error', title, message, 7000); // Erros ficam mais tempo
    }, [showToast]);

    const showSuccess = useCallback((title: string, message?: string) => {
        showToast('success', title, message, 4000);
    }, [showToast]);

    const showWarning = useCallback((title: string, message?: string) => {
        showToast('warning', title, message, 5000);
    }, [showToast]);

    const showInfo = useCallback((title: string, message?: string) => {
        showToast('info', title, message, 4000);
    }, [showToast]);

    return (
        <ToastContext.Provider value={{ showToast, showError, showSuccess, showWarning, showInfo }}>
            {children}

            {/* Container dos Toasts - canto superior direito */}
            <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-auto">
                {toasts.map(toast => (
                    <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
                ))}
            </div>

            {/* Estilos de animação */}
            <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out forwards;
        }
      `}</style>
        </ToastContext.Provider>
    );
};
